import React from 'react';
import { Button } from './ui/button';
import {
  Eye,
  GraduationCap,
  Package,
  ShieldCheck,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const projects = [
  {
    icon: Eye,
    industry: "Salmonicultura",
    title: "Sistema de Conteo con Vision Artificial",
    description: "Plataforma de conteo automatizado de especimenes utilizando computer vision e inteligencia artificial.",
    features: [
      "Deteccion y conteo en tiempo real",
      "Precision superior al 95%",
      "Reduccion de errores manuales"
    ],
    techStack: ["Python", "Computer Vision", "IA"],
    number: "01"
  },
  {
    icon: GraduationCap,
    industry: "Educacion Superior",
    title: "Plataforma de Evaluacion con IA",
    description: "Sistema integral para instituciones universitarias que evalua codigo y documentos con agentes de IA.",
    features: [
      "Revision automatica de codigo",
      "Feedback personalizado",
      "Nota sugerida para el docente",
      "Historial completo guardado"
    ],
    techStack: ["React", "Python", "Agentes IA"],
    number: "02"
  },
  {
    icon: Package,
    industry: "Logistica",
    title: "Sistema de Gestion de Inventario",
    description: "Solucion completa para el control y seguimiento de inventario con monitoreo inteligente.",
    features: [
      "Control de stock en tiempo real",
      "Alertas de reposicion",
      "Reportes automaticos"
    ],
    techStack: ["React", "APIs", "Dashboard"],
    number: "03"
  },
  {
    icon: ShieldCheck,
    industry: "Seguridad",
    title: "Control de Acceso con IA",
    description: "Sistema de gestion y monitoreo de acceso potenciado con inteligencia artificial.",
    features: [
      "Monitoreo 24/7 con IA",
      "Deteccion de anomalias",
      "Registro automatico de accesos"
    ],
    techStack: ["Computer Vision", "IA", "Dashboard"],
    number: "04"
  }
];

export const ProjectsSection = () => {
  const scrollToContact = () => {
    document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="proyectos" className="py-24 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <span className="text-sm font-medium text-accent uppercase tracking-[0.2em] mb-4 block">
              Proyectos Realizados
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground leading-tight text-balance">
              Experiencia en proyectos reales
            </h2>
          </div>
          <p className="text-muted-foreground text-lg max-w-md leading-relaxed">
            Soluciones que hemos desarrollado para diferentes industrias con resultados medibles.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {projects.map((project, index) => {
            const IconComponent = project.icon;
            return (
              <div
                key={index}
                className="group bg-card border border-border rounded-2xl p-8 lg:p-10 transition-all duration-500 hover:border-foreground/20 hover:shadow-lg"
              >
                {/* Top row: number + industry badge */}
                <div className="flex items-center justify-between mb-6">
                  <span className="text-sm font-medium text-muted-foreground/40">
                    {project.number}
                  </span>
                  <span className="inline-block px-3 py-1 bg-foreground/5 text-foreground/70 text-xs font-medium rounded-full tracking-wide">
                    {project.industry}
                  </span>
                </div>

                {/* Icon & Title */}
                <div className="flex items-start gap-5 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-foreground/5 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/10 transition-colors duration-300">
                    <IconComponent className="h-6 w-6 text-foreground group-hover:text-accent transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      {project.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {project.description}
                    </p>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-2.5 mb-6 pl-[76px]">
                  {project.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm text-foreground/70">
                      <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2 pl-[76px] pt-6 border-t border-border">
                  {project.techStack.map((tech, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-foreground/5 text-muted-foreground text-xs font-medium rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-muted-foreground mb-6 text-lg">
            Tienes un proyecto similar en mente?
          </p>
          <Button
            onClick={scrollToContact}
            className="bg-foreground text-background hover:bg-foreground/90 rounded-full px-8 py-3 text-base font-medium transition-all duration-300 group"
          >
            Conversemos sobre tu proyecto
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Button>
        </div>
      </div>
    </section>
  );
};
